import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import {
  BarChart3, TrendingUp, AlertTriangle, Shield, Users, DollarSign, BookOpen, Building, ShieldAlert, Layers, Briefcase
} from "lucide-react";

import MorosidadAE from "./reportes/MorosidadAE";
import MinimoTributable from "./reportes/MinimoTributable";
import MayorDetallado from "./reportes/MayorDetallado";
import MayorResumen from "./reportes/MayorResumen";
import RecaudacionPeriodo from "./reportes/RecaudacionPeriodo";
import PublicidadMorosa from "./reportes/PublicidadMorosa";
import AmbulantesMorosos from "./reportes/AmbulantesMorosos";
import MorosidadInmobiliaria from "./reportes/MorosidadInmobiliaria";
import MorosidadConformidad from "./reportes/MorosidadConformidad";
import MorosidadGeneral from "./reportes/MorosidadGeneral";
import RecaudacionCodPres from "./reportes/RecaudacionCodPres";
import RecaudacionActEco from "./reportes/RecaudacionActEco";
import RecaudacionClasInm from "./reportes/RecaudacionClasInm";
import PlantaValoresTierra from "./reportes/PlantaValoresTierra";
import TablaValoresConstruccion from "./reportes/TablaValoresConstruccion";
import RecaudacionActividadEspecificaRpt from "./reportes/RecaudacionActividadEspecifica";

const REPORT_COMPONENTS = {
  "morosidad-actividades-economicas": MorosidadAE,
  "minimo-tributable-lae": MinimoTributable,
  "mayor-impuesto-detallado": MayorDetallado,
  "mayor-impuesto-resumen": MayorResumen,
  "recaudacion": RecaudacionPeriodo,
  "publicidad-morosa": PublicidadMorosa,
  "ambulantes-morosos": AmbulantesMorosos,
  "morosidad-inmobiliaria-lae": MorosidadInmobiliaria,
  "morosidad-conformidad-uso": MorosidadConformidad,
  "morosidad-general-lae": MorosidadGeneral,
  "recaudacion-codigo-presupuestario": RecaudacionCodPres,
  "recaudacion-actividad-economica": RecaudacionActEco,
  "recaudacion-clasificador-inmuebles": RecaudacionClasInm,
  "planta-valores-tierra": PlantaValoresTierra,
  "tabla-valores-construccion": TablaValoresConstruccion,
  "recaudacion-actividad-especifica": RecaudacionActividadEspecificaRpt,
};

const modules = [
  { id: "morosidad-actividades-economicas", title: "Morosidad Actividades Económicas", description: "Contribuyentes con LAE con meses pendientes de declarar", icon: AlertTriangle, color: "red" },
  { id: "minimo-tributable-lae", title: "Mínimo Tributable LAE", description: "Contribuyentes con LAE que declararon mínimo tributable", icon: DollarSign, color: "amber" },
  { id: "recaudacion", title: "Recaudación por Período", description: "Análisis de recaudación por mes/trimestre/año", icon: TrendingUp, color: "green" },
  { id: "mayor-impuesto-detallado", title: "Mayor de Impuesto Detallado", description: "Desglose detallado de impuestos y tasas por planilla con filtros de fecha, estatus y oficina", icon: BookOpen, color: "indigo" },
  { id: "mayor-impuesto-resumen", title: "Mayor de Impuesto Resumen", description: "Agrupación por código presupuestario e impuesto/tasa con totales de multas, descuento y declarado", icon: BarChart3, color: "violet" },
  { id: "publicidad-morosa", title: "Publicidad Morosa", description: "Contribuyentes con avisos de publicidad con trimestres pendientes de pago", icon: AlertTriangle, color: "orange" },
  { id: "ambulantes-morosos", title: "Morosidad Vendedores Ambulantes", description: "Vendedores ambulantes con meses pendientes de pago", icon: Users, color: "teal" },
  { id: "morosidad-inmobiliaria-lae", title: "Morosidad Propiedad Inmobiliaria con LAE", description: "Propiedades de contribuyentes con LAE con años de solvencia pendientes", icon: BookOpen, color: "indigo" },
  { id: "morosidad-conformidad-uso", title: "Morosidad Conformidad de Uso Comercial", description: "Contribuyentes con LAE con Conformidad de Uso pendiente del año en curso", icon: ShieldAlert, color: "violet" },
  { id: "morosidad-general-lae", title: "Morosidad General con LAE", description: "Estado integral de morosidad: Act. Económicas, Publicidad, Solvencia Inmobiliaria y Conformidad de Uso", icon: Shield, color: "slate" },
  { id: "recaudacion-codigo-presupuestario", title: "Recaudación por Código Presupuestario", description: "Ingresos recaudados agrupados por código presupuestario", icon: DollarSign, color: "emerald" },
  { id: "recaudacion-actividad-economica", title: "Recaudación por Código Actividad Económica", description: "Ingresos recaudados agrupados por código y tipo de actividad económica", icon: BarChart3, color: "sky" },
  { id: "recaudacion-clasificador-inmuebles", title: "Recaudación por Clasificador de Inmuebles", description: "Ingresos recaudados agrupados por uso del inmueble y alícuota anual", icon: Building, color: "rose" },
  { id: "planta-valores-tierra", title: "Planta de Valores de la Tierra", description: "Clasificación del terreno con TCMMV/m2 y fichas catastrales asociadas", icon: Layers, color: "emerald" },
  { id: "tabla-valores-construccion", title: "Tabla de Valores de la Construcción (TVC)", description: "Dashboard de tipologías constructivas con valores por categoría, cobertura, gráficos y análisis", icon: Building, color: "blue" },
  { id: "recaudacion-actividad-especifica", title: "Recaudación por Actividad Específica", description: "Dashboard de recaudación de contribuyentes con LAE agrupado por giro comercial (panadería, restaurante, etc.) con gráficos y análisis", icon: Briefcase, color: "purple" },
  { id: "auditoria", title: "Auditoría", description: "Reportes de control interno", icon: Shield, color: "slate", disabled: true },
];

const ReportesPage = () => {
  const { subreporte } = useParams();
  const navigate = useNavigate();

  if (!subreporte) {
    return (
      <div className="space-y-6" data-testid="reportes-page">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
            <BarChart3 className="w-6 h-6 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Reportes
            </h1>
            <p className="text-slate-500">Análisis y estadísticas del sistema</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {modules.map((module, index) => {
            const Icon = module.icon;
            return (
              <Card 
                key={index} 
                className={`card-hover cursor-pointer group ${module.disabled ? 'opacity-50' : ''}`}
                onClick={() => !module.disabled && navigate(`/reportes/${module.id}`)}
                data-testid={`reporte-module-${module.id}`}
              >
                <CardContent className="p-6">
                  <div className={`w-12 h-12 bg-${module.color}-100 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <Icon className={`w-6 h-6 text-${module.color}-600`} />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1">{module.title}</h3>
                  <p className="text-sm text-slate-500">{module.description}</p>
                  {module.disabled && (
                    <span className="inline-block mt-2 text-xs bg-slate-100 text-slate-500 px-2 py-1 rounded">
                      Próximamente
                    </span>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    );
  }

  const ReportComponent = REPORT_COMPONENTS[subreporte];
  if (ReportComponent) {
    return <ReportComponent />;
  }

  return null;
};

export default ReportesPage;
